import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../contexts/LanguageContext';
import { motion } from 'framer-motion';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock,
  Send,
  MessageCircle,
  User,
  Building2,
  CheckCircle
} from 'lucide-react';

const Contact = () => {
  const { t } = useTranslation();
  const { language, isRTL } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const contactInfo = [
    {
      icon: Phone,
      title: t('contact.phone'),
      details: ['+971 50 123 4567', '+971 4 567 8901'],
      color: 'from-blue-500 to-blue-600'
    },
    {
      icon: Mail,
      title: t('contact.email'),
      details: ['info@nooraltayseer.ae', 'sales@nooraltayseer.ae'],
      color: 'from-green-500 to-green-600'
    },
    {
      icon: MapPin,
      title: t('contact.address'),
      details: [
        language === 'en' 
          ? 'Dubai Business District' 
          : 'منطقة دبي التجارية',
        language === 'en' 
          ? 'Dubai, United Arab Emirates' 
          : 'دبي، الإمارات العربية المتحدة'
      ],
      color: 'from-purple-500 to-purple-600'
    },
    {
      icon: Clock,
      title: t('contact.hours'),
      details: [
        language === 'en' ? 'Sun - Thu: 8:00 AM - 6:00 PM' : 'الأحد - الخميس: 8:00 ص - 6:00 م',
        language === 'en' ? 'Saturday: 8:00 AM - 2:00 PM' : 'السبت: 8:00 ص - 2:00 م',
        language === 'en' ? 'Friday: Closed' : 'الجمعة: مغلق'
      ],
      color: 'from-orange-500 to-orange-600'
    }
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        message: ''
      });
      
      // Reset success message after 5 seconds
      setTimeout(() => {
        setIsSubmitted(false);
      }, 5000);
    }, 2000);
  };

  return (
    <div className="min-h-screen pt-32 pb-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-900 via-blue-800 to-purple-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-islamic-pattern opacity-10" />
        <div className="container mx-auto px-4 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className={`max-w-4xl mx-auto ${isRTL ? 'font-arabic' : ''}`}
          >
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              {t('contact.title')}
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              {t('contact.subtitle')}
            </p>
            <div className="flex justify-center">
              <div className="flex items-center space-x-6 rtl:space-x-reverse">
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <Phone className="w-5 h-5 text-yellow-400" />
                  <span>+971 50 123 4567</span>
                </div>
                <div className="flex items-center space-x-2 rtl:space-x-reverse">
                  <Mail className="w-5 h-5 text-yellow-400" />
                  <span>info@nooraltayseer.ae</span>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -5, scale: 1.02 }}
                className="group"
              >
                <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 h-full">
                  <div className={`w-12 h-12 bg-gradient-to-r ${info.color} rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300`}>
                    <info.icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className={`text-xl font-bold text-gray-900 mb-3 ${isRTL ? 'font-arabic text-right' : ''}`}>
                    {info.title}
                  </h3>
                  <div className={`space-y-2 ${isRTL ? 'text-right' : ''}`}>
                    {info.details.map((detail, idx) => (
                      <p key={idx} className="text-gray-600 text-sm">
                        {detail}
                      </p>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Map */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className={`${isRTL ? 'font-arabic' : ''}`}
            >
              <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 shadow-lg">
                <div className="flex items-center space-x-3 rtl:space-x-reverse mb-6">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center">
                    <MessageCircle className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-3xl font-bold text-gray-900">
                    {language === 'en' ? 'Send us a Message' : 'أرسل لنا رسالة'}
                  </h2>
                </div>

                {isSubmitted && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded-lg mb-6 flex items-center space-x-2 rtl:space-x-reverse"
                  >
                    <CheckCircle className="w-5 h-5" />
                    <span>
                      {language === 'en' 
                        ? 'Thank you! Your message has been sent successfully.' 
                        : 'شكراً لك! تم إرسال رسالتك بنجاح.'
                      }
                    </span>
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                        {t('contact.form.name')} *
                      </label>
                      <div className="relative">
                        <User className={`absolute top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          required
                          className={`w-full ${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300`}
                          placeholder={t('contact.form.name')}
                        />
                      </div>
                    </div>

                    <div>
                      <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                        {t('contact.form.phone')}
                      </label>
                      <div className="relative">
                        <Phone className={`absolute top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className={`w-full ${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300`}
                          placeholder={t('contact.form.phone')}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                      {t('contact.form.email')} *
                    </label>
                    <div className="relative">
                      <Mail className={`absolute top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className={`w-full ${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300`}
                        placeholder={t('contact.form.email')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                      {t('contact.form.company')}
                    </label>
                    <div className="relative">
                      <Building2 className={`absolute top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400 ${isRTL ? 'right-3' : 'left-3'}`} />
                      <input
                        type="text"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className={`w-full ${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4'} py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300`}
                        placeholder={t('contact.form.company')}
                      />
                    </div>
                  </div>

                  <div>
                    <label className={`block text-sm font-semibold text-gray-700 mb-2 ${isRTL ? 'text-right' : ''}`}>
                      {t('contact.form.message')} *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className={`w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none ${isRTL ? 'text-right' : ''}`}
                      placeholder={t('contact.form.message')}
                    />
                  </div>

                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:from-blue-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 rtl:space-x-reverse"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" />
                        <span>
                          {language === 'en' ? 'Sending...' : 'جاري الإرسال...'}
                        </span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>{t('contact.form.submit')}</span>
                      </>
                    )}
                  </motion.button>
                </form>
              </div>
            </motion.div>

            {/* Map & Additional Info */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="space-y-8"
            >
              {/* Map */}
              <div className="bg-gray-200 rounded-2xl overflow-hidden shadow-lg h-96">
                <img
                  src="https://images.unsplash.com/photo-1721448289286-9c38459a3df6"
                  alt="Dubai Office Location"
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Quick Contact */}
              <div className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-8 shadow-lg">
                <h3 className={`text-2xl font-bold text-gray-900 mb-6 ${isRTL ? 'font-arabic text-right' : ''}`}>
                  {language === 'en' ? 'Quick Contact' : 'تواصل سريع'}
                </h3>
                
                <div className="space-y-4">
                  <a
                    href="tel:+971501234567"
                    className="flex items-center space-x-4 rtl:space-x-reverse p-4 bg-white rounded-lg hover:shadow-md transition-all duration-300 group"
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                    <div className={`${isRTL ? 'text-right' : ''}`}>
                      <p className="font-semibold text-gray-900">
                        {language === 'en' ? 'Call Us Now' : 'اتصل بنا الآن'}
                      </p>
                      <p className="text-gray-600">+971 50 123 4567</p>
                    </div>
                  </a>

                  <a
                    href="mailto:info@nooraltayseer.ae"
                    className="flex items-center space-x-4 rtl:space-x-reverse p-4 bg-white rounded-lg hover:shadow-md transition-all duration-300 group"
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                    <div className={`${isRTL ? 'text-right' : ''}`}>
                      <p className="font-semibold text-gray-900">
                        {language === 'en' ? 'Email Us' : 'راسلنا عبر البريد'}
                      </p>
                      <p className="text-gray-600">info@nooraltayseer.ae</p>
                    </div>
                  </a>

                  <a
                    href="https://wa.me/971501234567"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-4 rtl:space-x-reverse p-4 bg-white rounded-lg hover:shadow-md transition-all duration-300 group"
                  >
                    <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <MessageCircle className="w-6 h-6 text-white" />
                    </div>
                    <div className={`${isRTL ? 'text-right' : ''}`}>
                      <p className="font-semibold text-gray-900">
                        {language === 'en' ? 'WhatsApp' : 'واتساب'}
                      </p>
                      <p className="text-gray-600">
                        {language === 'en' ? 'Quick Response' : 'استجابة سريعة'}
                      </p>
                    </div>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className={`text-center mb-16 ${isRTL ? 'font-arabic' : ''}`}
          >
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {language === 'en' ? 'Frequently Asked Questions' : 'الأسئلة الشائعة'}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              {language === 'en' 
                ? 'Quick answers to common questions about our products and services'
                : 'إجابات سريعة للأسئلة الشائعة حول منتجاتنا وخدماتنا'
              }
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto">
            {[
              {
                question: language === 'en' ? 'What areas do you deliver to?' : 'ما هي المناطق التي توصلون إليها؟',
                answer: language === 'en' 
                  ? 'We deliver throughout the UAE, including Dubai, Abu Dhabi, Sharjah, Ajman, Ras Al Khaimah, Fujairah, and Umm Al Quwain.'
                  : 'نوصل في جميع أنحاء دولة الإمارات، بما في ذلك دبي وأبو ظبي والشارقة وعجمان ورأس الخيمة والفجيرة وأم القيوين.'
              },
              {
                question: language === 'en' ? 'Do you offer bulk discounts?' : 'هل تقدمون خصومات للكميات الكبيرة؟',
                answer: language === 'en' 
                  ? 'Yes, we offer competitive pricing for bulk orders. Contact us for a customized quote based on your project requirements.'
                  : 'نعم، نقدم أسعار تنافسية للطلبات الكبيرة. اتصل بنا للحصول على عرض سعر مخصص بناءً على متطلبات مشروعك.'
              },
              {
                question: language === 'en' ? 'What is your delivery timeframe?' : 'ما هي المدة الزمنية للتسليم؟',
                answer: language === 'en' 
                  ? 'Standard delivery is 2-5 business days within Dubai and 3-7 business days for other emirates, depending on product availability.'
                  : 'التسليم القياسي يستغرق 2-5 أيام عمل داخل دبي و3-7 أيام عمل للإمارات الأخرى، حسب توفر المنتج.'
              }
            ].map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-lg shadow-md p-6 mb-4 hover:shadow-lg transition-shadow duration-300"
              >
                <h3 className={`text-lg font-bold text-gray-900 mb-3 ${isRTL ? 'font-arabic text-right' : ''}`}>
                  {faq.question}
                </h3>
                <p className={`text-gray-600 leading-relaxed ${isRTL ? 'font-arabic text-right' : ''}`}>
                  {faq.answer}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;